import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';
import {
    OrderDeliveryInfo,
    OrderUserInfo,
    OrderImgInfo,
    OrderChargeInfo,
    OrderUserCompensateInfo,
    OrderRemindRecord,
    OrderLossCompensateInfo,
    OrderDutyCompensateInfo,
    DispatcherEvaluationResult,
    OrderRefundInfo,
    RefundTips,
    OrderOtherInfo,
    PaotuiPoiRightsInfo,
    OrderCustomerServiceCompensateInfo,
    CrossOrderCardBtnType,
    RawOrderBaseInfo,
} from '../types';

/** 一个经纬度坐标，为了保持精度，经纬度数值被放大了 1000000 倍 */
type Coordinate = {
    /** 经度 */
    longitude: number;
    /** 纬度 */
    latitude: number;
};

// 拼好饭迁移接口前缀
const PHF_URL_PREFIX = '/gw/phf';

/** 查询配送信息 */
const getDeliveryInfo = (orderInfos: string) => {
    return request.rawGet<{
        code: number;
        data: { [index: string]: OrderDeliveryInfo };
        dispatcherPhone: boolean;
        thirdDispatchAsyncTime: number;
        expressInfo: any;
    }>(`${PHF_URL_PREFIX}/v2/order/receive/processed/r/distribute/list/v2`, {
        params: {
            orderInfos,
        },
    });
};

/** 查询订单其他信息 */
const getOtherInfo = (orderInfos: string) => {
    return request.get<{ [index: string]: OrderOtherInfo }>(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/r/orderAsyncInfos/v3`,
        {
            params: {
                orderInfos,
            },
        },
    );
};

/** 请求订单退款信息 */
const getOrderRefundInfo = (orderInfos: string) => {
    const url = `${PHF_URL_PREFIX}/v2/order/refund/list/v2`;
    return request.rawGet<{
        code: number;
        data: { [index: string]: OrderRefundInfo };
        msg: string;
        /** 退款金额计价规则 */
        tip: string;
        /** 各类提示文案 */
        tips: RefundTips;
    }>(url, {
        params: {
            orderInfos,
        },
    });
};

/** 查询订单用户信息（收藏门店、外卖会员） */
const getOrderUserInfo = (userIdAndPoiIds: string) => {
    return request.get<OrderUserInfo[]>('/v2/order/receive/processed/r/favorites', {
        params: {
            userIdAndPoiIds,
        },
    });
};

/** 查询用户真实手机号、收货地址 */
const getRealPhoneAddress = (params: { orderViewId: string; wmPoiId: number }) => {
    return request.get<{
        Recipient_Phone: string;
        Recipient_bindedPhone: string;
        Recipient_Address: string;
    }>('/gw/phf/v2/order/receive/processed/r/querywithin3h', {
        params,
    });
};
/** 查询订单距离信息 */
const getOrderDistanceInfo = (orderLatAndLngS: string) => {
    return request.get<Array<{ wm_order_id_view_str: string; distance: number }>>(
        '/v2/order/receive/processed/r/distribute/pathDistance/v2',
        {
            params: {
                orderLatAndLngS,
            },
        },
    );
};

/** 获取订单隐私图片 */
const getOrderImgInfo = (orderInfos: string) => {
    return request.get<{ [index: string]: OrderImgInfo }>('/v2/order/history/r/getImageString', {
        params: {
            orderInfos,
        },
    });
};

const getChargeInfo = (chargeInfo: string) => {
    return request.postAsFormSubmit<OrderChargeInfo[]>(
        `${PHF_URL_PREFIX}/v2/order/receive/r/chargeInfo`,
        {
            chargeInfo,
        },
    );
};

/** 自配门店获取配送范围（接口返回的是JSON字符串） */
const getPoiShippingArea = (params: { wmPoiId: number; orderTime: number }) => {
    return request.get<string>('/api/poi/spArea', {
        params,
    });
};

type ConfirmOrderRawResponse = {
    msg: string;
    code: number;
    data: {
        distribute_tips?: string;
        cancel_reason: string;
        orderId: number;
        pay_status: number;
        pay_status_desc: string;
        status: number;
    };
};

type ConfirmOrderParams = {
    wmPoiId: number;
    orderId: string;
    acctId: string;
    token: string;
    isPrint: 0 | 1;
    appType: '3';
    isAutoAccept: 0 | 1;
};

/** 接单 */
const confirmOrder = (params: ConfirmOrderParams) => {
    return request.rawPostAsFormSubmit<ConfirmOrderRawResponse>(
        `${PHF_URL_PREFIX}/v2/order/receive/unprocessed/w/confirm`,
        params,
    );
};

type PartialRefundFoodInfo = {
    boxNum: number;
    boxPrice: number;
    count: number;
    foodName: string;
    foodPrice: number;
    imageUrl: string;
    itemId: string;
    originFoodPrice: number;
    refundPrice: number;
    wmFoodId: number;
};

type GetPartialRefundFoodListResponse = {
    marking: number;
    foodInfos: PartialRefundFoodInfo[];
};

/** 获取部分退款商品列表 */
const getPartialRefundFoodList = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<GetPartialRefundFoodListResponse>('/v2/order/refund/partRefund/foodInfo', {
        params: {
            wmPoiId,
            wmOrderViewId,
        },
    });
};

type PartialRefundCalculateFood = {
    itemId: string;
    wmFoodId: number;
    count: number;
};

/** 计算部分退款金额 */
const calculatePartialRefundAmount = (
    wmPoiId: number,
    wmOrderViewId: string,
    foodInfos: PartialRefundCalculateFood[],
) => {
    return request.postAsFormSubmit<{ refundMoney: number; hasBoxFee: boolean }>(
        `${PHF_URL_PREFIX}/v2/order/refund/partRefund/calPrice`,
        {
            wmPoiId,
            wmOrderViewId,
            foodInfos: JSON.stringify(foodInfos),
        },
    );
};

/** 提交部分退款申请 */
const applyPartialRefund = (
    wmPoiId: number,
    wmOrderViewId: string,
    reason: string,
    foodInfos: PartialRefundCalculateFood[],
) => {
    return request.postAsFormSubmit(`${PHF_URL_PREFIX}/v2/order/refund/partRefund/apply`, {
        wmPoiId,
        wmOrderViewId,
        reason,
        foodInfos: JSON.stringify(foodInfos),
    });
};

/** 获取门店营业状态 */
const getPoiBusinessStatus = (poiId: number, orderId: string) => {
    return request.get<number>('/common/status', {
        params: {
            orderId,
            poiId,
        },
    });
};

type CancelOrderReason = {
    cat_id: number;
    id: number;
    remark: string;
    sort: 0;
};

/** 获取取消订单原因列表 */
const getCancelOrderReasonList = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<CancelOrderReason[]>('/v2/order/receive/cancelReasons/r/get', {
        params: {
            appType: '3',
            wmPoiId,
            wmOrderViewId,
        },
    });
};

/** 获取取消订单时可同步下架的商品列表 */
const getCancelReasonFoodList = (wmPoiId: number, orderId: string) => {
    return request.postAsFormSubmit<Array<{ id: number; name: string }>>(
        '/api/order/cancelReasons/foods',
        {
            wmPoiId,
            orderId,
            token: Ids.getToken(),
            acctId: Ids.getAcctId(),
        },
    );
};

type CancelOrderParams = {
    appType: '3';
    token: string;
    acctId: string;
    orderId: string;
    wmPoiId: number;
    reasonId: number;
    subRadioId: string;
    remark: string;
    isInNewOrder: 1 | 0;
};

/** 取消订单 */
const cancelOrder = (params: Omit<CancelOrderParams, 'appType' | 'token' | 'acctId'>) => {
    return request.postAsFormSubmit(`${PHF_URL_PREFIX}/v2/order/receive/unprocessed/w/cancel`, {
        ...params,
        appType: '3',
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
    });
};

/** 查询订单用户赔付信息 */
const getUserCompensateInfo = (orderInfos: string) => {
    return request.get<{
        refundRule1: string;
        refundRule2: string;
        refundAppeal1: string;
        refundAppeal2: string;
        deductionType1: string;
        deductionType2: string;
        appealTips: string;
        wmEapiCompensationVoList: OrderUserCompensateInfo[];
    }>('/v2/order/refund/system/r/list', { params: { orderInfos } });
};

/** 查询订单催单记录 */
const getOrderRemindInfo = (orderInfos: string) => {
    return request.get<{ [index: string]: OrderRemindRecord[] }>(
        '/v2/order/customer/reminder/list/v2',
        { params: { orderInfos } },
    );
};

type ImCapability = {
    /** 用户侧APP版本是否支持 */
    userVersionSupport: number;
};
/** 查询商家侧发起IM能力 */
const getImCapability = (params: {
    acctId: string;
    uuid: string;
    wmPoiId: number;
    wmUserId: number;
    orderViewId: string;
}) => {
    return request.get<ImCapability>('/v2/chat/im/launch', { params });
};

/** 获取催单回复通用文案 */
const getOrderRemindExplaination = () => {
    return request.rawGet<{ data: string[] }>('/v2/order/customer/reminder/unprocessed/r/exlain');
};

type ReplyRemindBaseParams = {
    wmPoiId: number;
    remindId: number;
    response: string;
};

/** 通过IM回复催单 */
const replyRemindThroughIM = (
    params: ReplyRemindBaseParams & { acctId: string; userId: number; orderViewId: string },
) => {
    return request.postAsFormSubmit(
        '/v2/order/customer/reminder/unprocessed/w/im/response',
        params,
    );
};

/** 不通过IM回复催单 */
const replyRemindWithoutIM = (params: ReplyRemindBaseParams & { wmOrderViewId: string }) => {
    return request.postAsFormSubmit(
        '/v2/order/customer/reminder/unprocessed/w/response/v2',
        params,
    );
};

/** 上报出餐完成 */
const reportPrepareMealCompleted = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.postAsFormSubmit(
        `${PHF_URL_PREFIX}/v2/common/w/reported/completeMealTime`,
        params,
    );
};

/** 查询订单餐损赔付信息 */
const getLossCompensateInfo = (orderInfos: string) => {
    return request.get<OrderLossCompensateInfo[]>(
        '/v2/order/receive/processed/r/distribute/payment/list/v2',
        {
            params: { orderInfos },
        },
    );
};

/** 餐损赔付申请详情 */
type LossCompensateDetail = {
    /** 赔付金额 */
    amount: number;
    /** 是否按比例赔付 */
    isDiscount: boolean;
    /** 赔付百分比 */
    discountRate: number;
};

/** 查询餐损赔付申请详情 */
const getLossCompensateDetailForOrder = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.get<LossCompensateDetail>(
        '/v2/order/receive/processed/r/distribute/payment/amount/v2',
        {
            params,
        },
    );
};

type SubmitLossCompensateApplyParams = {
    wmOrderViewId: string;
    wmPoiId: number;
    applicationReason: string;
    amount: number;
    applyStatus: number;
};

/** 提交 餐损赔付/赔付申诉 */
const submitLossCompensateApply = (params: SubmitLossCompensateApplyParams) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/payment/apply/v2',
        params,
    );
};

/** 撤销 餐损赔付/赔付申诉 */
const cancelLossCompensateApply = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/payment/cancel/v2',
        params,
    );
};

/** 查询订单定责赔付信息 */
const getDutyCompensateInfo = (orderInfos: string) => {
    const url = `${PHF_URL_PREFIX}/v2/order/confirm/responsibility/r/query`;
    return request.get<{ [key: string]: OrderDutyCompensateInfo }>(url, { params: { orderInfos } });
};

type SubmitDutyCompensateApplyParams = {
    wmOrderViewId: string;
    wmPoiId: number;
    reason: string;
    picUrls: string;
};

/** 提交定责赔付申诉 */
const submitDutyCompensateApply = (params: SubmitDutyCompensateApplyParams) => {
    const url = `${PHF_URL_PREFIX}/v2/order/confirm/responsibility/w/appeal`;
    return request.postAsFormSubmit(url, params);
};

/** 获取骑手电话列表 */
const getDispatcherPhoneList = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/processed/r/dispatcherPhone`;
    return request.get<{
        dispatcherPhone: string;
        orgLeaderPhone: string;
        orgEmergencyPhone: string;
        showTips: boolean;
        detailTips: string;
    }>(url, {
        params,
    });
};

/** 获取配送状态变更日志 */
const getDeliveryStatusLog = (params: { wmPoiIdFromQuery: number; wmOrderViewId: string }) => {
    const url = `${PHF_URL_PREFIX}/api/logistics/logs`;
    return request.get<Array<{ timeLine: string; deliveryStatusDesc: string }>>(url, { params });
};

/** 获取骑手轨迹 */
const getDispatcherTraceInfo = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/r/distribute/getTraceInfo`;
    return request.get<Array<{ lat: number; lng: number }>>(url, { params });
};

/** 骑手点击到店状态 */
type DispatcherArrivedStatus = {
    /** 骑手头像url */
    avatar: string;
    /** 骑手点击到店时距离门店的距离 */
    distance?: number;
    /** 允许骑手点击到店的距离门店的最大距离，超出此距离视为异常给商家提示 */
    limit: number;
    /** 门店头像url */
    shopPicUrl: string;
    /** 骑手点击到店位置的坐标 */
    locationInfo?: Coordinate;
};
/** 获取骑手的到店状态信息 */
const getDispatcherArrivedStatus = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/r/distribute/getArrivedStatus`;
    return request.get<DispatcherArrivedStatus>(url, {
        params,
    });
};

/** 发起配送（跑腿非灰度、三方、美配）*/
const sendDelivery = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    deliveryTeamId: number;
    tipAmount: number;
    shippingFee: number;
}) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/v2`,
        params,
    );
};

/** 商家自配（点击”自己送餐“） */
const deliverBySelf = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/w/sent/v2`,
        params,
    );
};

/** 跑腿灰度商家活动、优惠券、权益详情 */
type PtGrayActivityCouponInfo = {
    /** 选中活动的金额 */
    activityAmount: number;
    /** 选中活动的ID */
    activityId: string;
    /** 选中活动的名称 */
    activityName: string | null;
    /** 选中优惠券的金额 */
    couponAmount: number;
    /** 可选优惠券数量 */
    couponCount: number;
    /** 选中优惠券的ID */
    couponId: string;
    /** 选中优惠券的名称 */
    couponName: string | null;
    couponSource: number;
    couponViewId: string;
    /** 临时加价 */
    extraFee: number;
    /** 临时加价说明文案 */
    extraFeeReason: string;
    /** 基础运费 */
    shippingFeeBase: number;
    shippingFee: number;
    bmCityId: number;
    distance: number;
    /** 跑腿资源位 */
    zbResourceInfoList?: Array<{
        resource_id: number;
        pic_url: string;
        click_url: string;
    }>;
    /** 跑腿权益 */
    logisticsRightDesc?: PaotuiPoiRightsInfo;
};

type GetPtGrayActivityCouponInfoParams =
    | {
          wmOrderViewId: string;
          wmPoiId: number;
          tipAmount: number;
          activityId: string;
          couponId: string;
      }
    | {
          wmOrderViewId: string;
          wmPoiId: number;
          tipAmount: number;
          activityId: string;
          couponViewId: string;
          couponSource: number;
      };

/** 跑腿灰度商家查询活动、优惠券信息 */
const getPtGrayActivityCouponInfo = (params: GetPtGrayActivityCouponInfoParams) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/query/r/pt/activity/info`;
    return request.rawGet<{
        code: number;
        msg: string;
        data: PtGrayActivityCouponInfo;
    }>(url, {
        params,
    });
};

/** 请求跑腿资源位banner列表 */
const getZhongbaoResourceInfo = (params: { wmPoidId: number }) => {
    return request.get<{
        zbResourceInfoList: Array<{ resource_id: number; pic_url: string; click_url: string }>;
    }>('/gw/v2/order/receive/query/r/pt/resource/info', { params });
};

/** 获取小费选项列表 */
const getDeliveryTipOptions = () => request.get<number[]>('/gw/api/tipFee/config');

/** 发起跑腿灰度 */
const sendZhongbaoForPtGray = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    tipAmount: number;
    shippingFee: number;
    couponParam: string;
}) => {
    return request.postAsFormSubmit(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/w/pt/distribute`,
        params,
    );
};

/** 获取跑腿非灰度配送费预览 */
const getZhongbaoNormalShippingFee = (
    orderInfos: Array<{ wmOrderViewId: string; wmPoiId: number }>,
) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/processed/r/distribute/shippingFee/v2`;
    return request.get<Array<{ wm_order_view_id: string; shipping_fee: number }>>(url, {
        params: {
            orderInfos: JSON.stringify(orderInfos),
        },
    });
};

type QuerySanfangShippingFeeResponse = {
    distance: number;
    /** 实付金额 */
    shippingFee: number;
    /** 费用明细 */
    shippingFeeDetail: Array<{
        /** 费用金额 */
        amount: number;
        /** 费用项 */
        shippingFeeDesc: string;
    }>;
    /** 预计送达时间 */
    estimatedDeliveryTime: string;
};

/** 三方配询价 */
const querySanfangShippingFee = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    thirdPartySpecificLogisticsId: number;
    optimizeVersion: 1;
}) => {
    return request.postAsFormSubmit<QuerySanfangShippingFeeResponse | null>(
        '/v2/order/receive/processed/r/third/distribution/preview',
        params,
    );
};

/** 获取取消配送的原因列表 */
const getCancelDeliveryReasonList = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<{
        title: string;
        deliveryStatus: number;
        reasonList: Array<{ code: string; content: string }>;
    }>('/v2/order/receive/processed/r/distribute/cancel/reason/v2', {
        params,
    });
};

type CancelDeliveryParams = {
    wmOrderViewId: string;
    wmPoiId: number;
    deliveryStatus: number | string;
    code: string;
    content: string;
    otherReason: string;
    optimizeVersion: number;
};
/** 取消配送 */
const cancelDelivery = (params: CancelDeliveryParams) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/cancel/v2`,
        params,
    );
};
/** 取消配送二次确认 */
const cancelDeliveryConfirm = (params: Omit<CancelDeliveryParams, 'optimizeVersion'>) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/cancel/confirm/v2`,
        params,
    );
};

/** 三方配取消配送异步查询结果 */
const querySanfangCancelDeliveryResult = (
    params: Omit<CancelDeliveryParams, 'optimizeVersion' | 'deliveryStatus'>,
) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/v2/order/receive/processed/w/distribute/cancel/queryResult',
        params,
    );
};

/** 发起自配 */
const changeToPoiSelfShipping = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit(
        `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/changeToPoiSelfShipping`,
        params,
    );
};

/** 查询骑手评价数据 */
const getDispatcherEvaluationData = (orderInfos: string) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/processed/r/distribute/evaluation/v2`;
    return request.rawGet<{
        code: number;
        evaluationList: DispatcherEvaluationResult[];
        structureds: { [index: number]: string };
    }>(url, {
        params: {
            orderInfos,
        },
    });
};

/** 提交对骑手的评价 */
const saveDispatcherEvaluation = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    grade: number;
    personal: string;
    structuredCodes: string;
}) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/evaluation/v2`;
    return request.postAsFormSubmit(url, params);
};

/** 跑腿、快送、三方加小费 */
const updateShippingTips = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    tipAmount: number;
    optimizeVersion: number;
    shippingFee?: number;
    specificLogisticsId?: number;
}) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/tipAmount/v2`;
    return request.postAsFormSubmit(url, params);
};

/** 三方配修改小费之前查询小费信息 */
const querySanfangTipsInfo = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<{
        tipAmount: number;
        tipfees: number[];
        shippingFee: number;
    }>('/v2/order/receive/processed/r/third/query/tipfee', {
        params,
    });
};

/** 三方配查询加小费结果 */
const querySanfangUpdateTipsResult = (params: {
    wmPoiId: number;
    wmOrderViewId: string;
    tipAmount: number;
}) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/tipAmount/queryResult',
        params,
    );
};

/** 提交投诉骑手 */
const submitDispatcherComplication = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    dispatcherName: string;
    dispatcherMobile: string;
    type: number;
    pics: string;
}) => {
    const url = `${PHF_URL_PREFIX}/v2/order/receive/processed/w/distribute/reportDeliveryException`;
    return request.postAsFormSubmit(url, params);
};

/** 获取退款预判责信息 */
const getRefundPreResponsiblityInfo = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.get<{
        /** 是否需要转人工定责 0=不需要,1=需要 */
        if_need_manual: 0 | 1;
        /** 责任方数字描述 */
        responsible_party: number;
        /** 责任方文字描述 */
        responsiblePartyDesc: string;
        /** 具体的定责原因 */
        reason: string;
        wm_order_view_id: string;
    } | null>(`${PHF_URL_PREFIX}/v2/order/confirm/responsibility/r/preConfirm`, {
        params,
    });
};

/** 获取拒绝退款的原因列表 */
const getRejectRefundReasonList = (params: {
    wmPoiId: number;
    orderId: string;
    logisticsCode: string;
    logisticsStatus: number;
}) => {
    return request.get<{ rejectReasonTips: string; rejectReasonList?: string[] }>(
        '/v2/order/refund/unprocessed/r/rejectReason',
        {
            params,
        },
    );
};

/** 提交拒绝退款 */
const submitRejectRefund = (params: { wmPoiId: number; orderId: string; rejectReason: string }) => {
    return request.postAsFormSubmit(`${PHF_URL_PREFIX}/v2/order/refund/unprocessed/w/reject`, {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/** 同意退款 */
const submitAgreeRefund = (params: { wmPoiId: number; orderId: string }) => {
    return request.postAsFormSubmit(`${PHF_URL_PREFIX}/v2/order/refund/unprocessed/w/agree`, {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/** 请求门店是否命中卡餐上报 */
const getMakeMealSlowReportGrayStatus = () => {
    return request.get<boolean>('/gw/api/r/waitMeal/poi/gray/new');
};

/** 请求门店是否出餐后调度 */
const getAfterMealDispatchStatus = () => {
    return request.post<boolean>('/gw/api/r/orderPressure/afterMeal/get');
};

/** 门店卡餐状态信息 */
type MakeMealSlowState = {
    /** 无卡餐生效时的功能引导文案 */
    configText: string;
    /** 主动上报按钮是否处于禁用状态（是否有卡餐生效中）1是，0否 */
    buttonForbidden: 0 | 1;
    /** 卡餐生效中提示文案 */
    optioninfo: string;
    /** 系统当前时间 */
    currentTimestamp: number;
    /** 当前卡餐状态结束时间 */
    endTimestamp: number;
};
/** 请求卡餐状态 */
const getMakeMealSlowState = () => {
    return request.get<MakeMealSlowState>('/gw/api/r/waitMeal/index/config/get');
};

type ExtendMakeMealTimeOption = {
    subOperationType: number;
    subOperationName: string;
};

type EffectTimeOption = {
    value: number;
    name: string;
};

type GetReportMakeMealSlowOptionsResponse = {
    /** 生效时间 */
    effectTime: {
        /** 选项 */
        operation: EffectTimeOption[];
        /** 默认值 */
        defaultValue: EffectTimeOption;
    };
    /** 支持的处理方式，原来有多重处理方式，现在只有延长出餐时间，为了保持兼容性，依然是一个数组 */
    operation: Array<{
        operationType: number;
        defaultValue: ExtendMakeMealTimeOption;
        subOperation: ExtendMakeMealTimeOption[];
    }>;
};

const getReportMakeMealSlowOptions = () => {
    return request.get<GetReportMakeMealSlowOptionsResponse>('/gw/api/v2/r/waitMeal/option');
};

const submitMakeMealSlowReport = (params: {
    sceneType: number;
    extendMakeMealTimeType: number;
    effectTime: number;
    expireTime?: string;
    balanceUuid?: string;
}) => {
    const data: any = {
        sceneType: params.sceneType,
        action: JSON.stringify([
            {
                operationType: 1,
                subOperationType: [params.extendMakeMealTimeType],
            },
        ]),
        effectTime: params.effectTime,
    };
    if (params.expireTime) {
        data.expireTime = params.expireTime;
    }

    if (params.balanceUuid) {
        data.balanceUuid = params.balanceUuid;
    }
    return request.postAsFormSubmit('/gw/api/v2/w/report/waitMeal', data);
};

type GetInsuranceRefundAdviceResponse = {
    /** 标题 */
    title: string;
    /** 内容 */
    content: Array<{
        /** 标题 */
        label: string;
        /** 内容 */
        value: string;
    }>;
    /** 赔付规则 */
    refundRule: {
        /** 标题 */
        name: string;
        /** 规则内容地址（图片） */
        detailPic: string;
    };
    /** 保障条款地址 */
    specUrl: string;
};

/** 餐损险保障详情 */
const getInsuranceRefundAdvice = () => {
    return request.postAsFormSubmit<GetInsuranceRefundAdviceResponse>(
        '/gw/api/order/insurance/refundTips',
        {
            acctId: Ids.getAcctId(),
            wmPoiId: Ids.getWmPoiId(),
            appType: 3,
        },
    );
};

/**
 * 查询(拼好饭拼单主/子)订单商服赔付信息
 */
const getCustomerServiceCompensateInfo = (orderInfos: string) => {
    return request.get<OrderCustomerServiceCompensateInfo[]>(
        `${PHF_URL_PREFIX}/v2/order/compensation/r/info`,
        {
            params: { orderInfos },
        },
    );
};

/** 餐车配送/重发 */
const fetchDiningCarShipping = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit(`${PHF_URL_PREFIX}/dingcar/sendLogistics`, params);
};

/** 跨店送订单提示文案 */
const fetchCrossStoreFightContent = (params: {
    wmPoiId: number;
    orderViewId: string;
    type: CrossOrderCardBtnType;
}) => {
    return request.postAsFormSubmit(`${PHF_URL_PREFIX}/get/crossStoreFightContent`, params);
};

interface ExamineInstructionResponse {
    title: string;
    type: string;
    content: {
        title: string;
        type: string;
        content: {
            type: string;
            label: string;
            content: string;
            subContent: string;
        }[];
    }[];
}

interface PerformanceInstrContent {
    title: string;
    content: ExamineInstructionResponse[];
}

/** 出餐表现考核说明 弹窗文本接口 */
const fetchPerformanceInstr = (wmPoiId: number) =>
    request.get<PerformanceInstrContent>(`${PHF_URL_PREFIX}/api/prepMeal/examine/instruction`, {
        params: {
            wmPoiId,
        },
    });

/** 获取订单部分模块详情 */
const getOneOrderDetail = (params: { orderViewId: string; wmPoiId: number; blockTags?: string }) =>
    request.rawPostAsFormSubmit<{
        code: number;
        data: RawOrderBaseInfo;
        msg: string;
        serverTime: number;
    }>('/gw/api/order/mix/detail/common', {
        ...params,
    });

export {
    getDeliveryInfo,
    getOtherInfo,
    getOrderUserInfo,
    getRealPhoneAddress,
    getOrderDistanceInfo,
    getOrderImgInfo,
    getPoiShippingArea,
    ConfirmOrderRawResponse,
    confirmOrder,
    getPartialRefundFoodList,
    PartialRefundFoodInfo,
    calculatePartialRefundAmount,
    applyPartialRefund,
    getPoiBusinessStatus,
    getCancelOrderReasonList,
    CancelOrderReason,
    getCancelReasonFoodList,
    cancelOrder,
    getChargeInfo,
    getUserCompensateInfo,
    getOrderRemindInfo,
    getImCapability,
    getOrderRemindExplaination,
    replyRemindThroughIM,
    replyRemindWithoutIM,
    reportPrepareMealCompleted,
    getLossCompensateInfo,
    LossCompensateDetail,
    getLossCompensateDetailForOrder,
    submitLossCompensateApply,
    cancelLossCompensateApply,
    getDutyCompensateInfo,
    submitDutyCompensateApply,
    getDispatcherPhoneList,
    getDeliveryStatusLog,
    getDispatcherTraceInfo,
    getDispatcherArrivedStatus,
    Coordinate,
    DispatcherArrivedStatus,
    sendDelivery,
    deliverBySelf,
    PtGrayActivityCouponInfo,
    getPtGrayActivityCouponInfo,
    getDeliveryTipOptions,
    sendZhongbaoForPtGray,
    getZhongbaoNormalShippingFee,
    querySanfangShippingFee,
    QuerySanfangShippingFeeResponse,
    getCancelDeliveryReasonList,
    cancelDelivery,
    cancelDeliveryConfirm,
    querySanfangCancelDeliveryResult,
    changeToPoiSelfShipping,
    getDispatcherEvaluationData,
    saveDispatcherEvaluation,
    updateShippingTips,
    querySanfangTipsInfo,
    querySanfangUpdateTipsResult,
    submitDispatcherComplication,
    getOrderRefundInfo,
    getRefundPreResponsiblityInfo,
    getRejectRefundReasonList,
    submitRejectRefund,
    submitAgreeRefund,
    getZhongbaoResourceInfo,
    getMakeMealSlowReportGrayStatus,
    getMakeMealSlowState,
    getReportMakeMealSlowOptions,
    ExtendMakeMealTimeOption,
    EffectTimeOption,
    submitMakeMealSlowReport,
    getAfterMealDispatchStatus,
    getInsuranceRefundAdvice,
    GetInsuranceRefundAdviceResponse,
    getCustomerServiceCompensateInfo,
    fetchDiningCarShipping,
    fetchCrossStoreFightContent,
    PerformanceInstrContent,
    ExamineInstructionResponse,
    fetchPerformanceInstr,
    getOneOrderDetail,
};
